/**
 * Market making's OpenMarkets model (docs: Models & attribution, /developers/models).
 *
 * Every order the Market making screen places is filed under one model, "greenbook · Market
 * making", so its quotes are the server's `GET /auth/opportunities?model_id=…` on any device
 * instead of a per-browser list.
 *
 *   read      `GET /auth/models` on page load (active models; `pickMmModel` finds ours). The id is
 *             cached in react-query under the auth identity (session-guard wipes it on a switch).
 *   create    lazily, at the first quote placement (`ensureMmModel`), never on page load: it
 *             writes to the account. `POST /auth/models` with a fixed Idempotency-Key so racing
 *             tabs/devices get one model (then a read confirms the id is this account's: the docs
 *             don't say how model keys are scoped, FRICTION.md). Needs trade:execute; a 4xx (no scope, 50-model cap,
 *             …) places the quote without a model instead of blocking it (it then lives only in
 *             this browser's registry, like the quotes from before models).
 *   list      the model's opportunities, every status, paged (`useMmServerQuotes`).
 */
import { useMemo } from "react";
import { useQuery, type QueryClient, type UseQueryResult } from "@tanstack/react-query";
import type { Model, Opportunity } from "@greenbook/client";
import { ApiError, apiGet, apiPost } from "./api.js";
import { SCOPES, useAccess, useAuthIdentity } from "./omauth.js";
import {
  isResting,
  MM_MODEL_DESCRIPTION,
  MM_MODEL_IDEMPOTENCY_KEY,
  MM_MODEL_NAME,
  pickMmModel,
  useMmRegistry,
  type MmLeg,
} from "./marketMaking.js";

const MIN = 60 * 1000;
/** Poll the list this often while a quote has resting bids (fills land), else the account pace. */
const POLL_RESTING_MS = 10_000;
const POLL_IDLE_MS = 30_000;
/** Opportunities per page (the most the endpoint has answered with) and pages read at most. */
const PAGE = 200;
const MAX_PAGES = 5;

const modelKey = (identity: string | null) => ["models", "market-making", identity] as const;

/** The account's Market making model id, or null when it has none yet. */
async function readMmModelId(signal?: AbortSignal): Promise<string | null> {
  const models = (await apiGet<Model[]>("/models", signal)).data;
  return pickMmModel(models)?.model_id ?? null;
}

/** The Market making model id: undefined while loading (or signed out), null when there is none yet. */
export function useMmModelQuery(): UseQueryResult<string | null> {
  const identity = useAuthIdentity();
  const allowed = useAccess(SCOPES.account).ok;
  return useQuery({
    queryKey: modelKey(identity),
    enabled: allowed && identity != null,
    queryFn: ({ signal }) => readMmModelId(signal),
    staleTime: 10 * MIN,
  });
}

/** What placing a new quote files it under: the model, or none (with why). */
export type EnsuredModel = { modelId: string; reason: null } | { modelId: null; reason: string };

const inflight = new Map<string, Promise<EnsuredModel>>();

/**
 * The Market making model's id, creating the model if the account has none. Call it only when
 * the user places a quote. Re-reads the list before creating (another tab or device may have
 * made it since the page loaded), and sends a fixed Idempotency-Key. An API refusal (4xx: no
 * trade:execute, the 50 active-model cap, …) resolves to no model so the quote is still placed;
 * anything else (network, 5xx) throws and nothing is placed.
 */
export function ensureMmModel(qc: QueryClient, identity: string | null): Promise<EnsuredModel> {
  const k = identity ?? "";
  const running = inflight.get(k);
  if (running) return running;
  const p = (async (): Promise<EnsuredModel> => {
    const key = modelKey(identity);
    const cached = qc.getQueryData<string | null>(key);
    if (cached) return { modelId: cached, reason: null };
    const found = await qc.fetchQuery({ queryKey: key, queryFn: ({ signal }) => readMmModelId(signal), staleTime: 0 });
    if (found) return { modelId: found, reason: null };
    const create = async (idempotencyKey: string) =>
      (await apiPost<Model>("/models", { model_name: MM_MODEL_NAME, model_description: MM_MODEL_DESCRIPTION }, undefined, { "Idempotency-Key": idempotencyKey })).data;
    try {
      let created = await create(MM_MODEL_IDEMPOTENCY_KEY);
      // The docs scope /orders/buy keys "to your workspace" and say nothing for models. If that
      // means greenbook's workspace rather than this account, the fixed key could replay another
      // account's model: check it's ours (a read), and if not, create with a key of our own.
      const ours = await apiGet<Model>(`/models/${encodeURIComponent(created.model_id)}`).then(
        () => true,
        (e: unknown) => {
          if (e instanceof ApiError && e.status === 404) return false;
          throw e;
        },
      );
      if (!ours) created = await create(`${MM_MODEL_IDEMPOTENCY_KEY}:${identity ?? "anon"}`);
      // A replayed key could hand back a model archived since: orders under it would 409.
      if (created.status && created.status !== "active") return { modelId: null, reason: `The model is ${created.status}.` };
      qc.setQueryData(key, created.model_id);
      void qc.invalidateQueries({ queryKey: ["opportunities", "mm-model"] });
      return { modelId: created.model_id, reason: null };
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.code !== "no_credential") {
        return { modelId: null, reason: e.message || e.code };
      }
      throw e;
    }
  })();
  inflight.set(k, p);
  void p.finally(() => inflight.delete(k)).catch(() => {});
  return p;
}

const hasResting = (opps: Opportunity[] | undefined): boolean =>
  (opps ?? []).some((o) => (o.legs ?? []).some((l) => isResting(l as MmLeg)));

/**
 * Every opportunity filed under the Market making model, all statuses (the docs list no status
 * values; open and closed are what rows carry), newest first, up to {@link MAX_PAGES} pages.
 * Under ["opportunities", …] so placements and the orders stream refresh it.
 */
export function useMmServerQuotes(modelId: string | null | undefined): UseQueryResult<Opportunity[]> {
  return useQuery({
    queryKey: ["opportunities", "mm-model", modelId],
    enabled: !!modelId,
    queryFn: async ({ signal }) => {
      const out: Opportunity[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < MAX_PAGES; page++) {
        const qs = new URLSearchParams({ model_id: modelId!, limit: String(PAGE) });
        if (cursor) qs.set("cursor", cursor);
        const res = await apiGet<Opportunity[]>(`/opportunities?${qs.toString()}`, signal);
        out.push(...res.data);
        cursor = res.pagination?.has_more ? res.pagination.next_cursor : null;
        if (!cursor) break;
      }
      return out;
    },
    staleTime: 5_000,
    refetchInterval: (q) => (hasResting(q.state.data) ? POLL_RESTING_MS : POLL_IDLE_MS),
  });
}

/**
 * Which opportunities are greenbook Market making quotes: this browser's registry plus every
 * one the server files under the model (placed on any device). Portfolio, History and the Desk
 * read it to label plays as quotes.
 */
export function useMmQuoteIds(): ReadonlySet<string> {
  const reg = useMmRegistry();
  const model = useMmModelQuery();
  const server = useMmServerQuotes(model.data);
  return useMemo(() => {
    const ids = new Set(reg.map((e) => e.opportunityId));
    for (const o of server.data ?? []) ids.add(o.opportunity_id);
    return ids;
  }, [reg, server.data]);
}
